/**
 * Сборка близких меток в одну.
 *
 * На карте страны заявки из Москвы и Химок — это одна точка шириной в
 * пиксель: метки ложатся друг на друга, счёт по ним не прочитать, а нажать
 * можно только верхнюю. Поэтому при отдалении соседние метки сливаются
 * в кружок с числом, а при приближении расходятся обратно.
 *
 * Сетка, а не «настоящая» кластеризация по расстоянию: результат зависит
 * только от масштаба и координат, поэтому карта не «дышит» при перерисовке
 * и не требует хранить состояние между кадрами.
 */

export interface Located {
  lat: number;
  lng: number;
}

export interface Cluster<T extends Located> {
  /** Центр кружка — среднее по участникам, чтобы он не съезжал с города. */
  lat: number;
  lng: number;
  items: T[];
}

/**
 * Размер ячейки в градусах для масштаба Leaflet.
 *
 * Считается от экранных пикселей, а не подбирается на глаз: в проекции
 * Меркатора мир на масштабе `z` имеет ширину 256·2^z пикселей, значит
 * градус долготы стоит `360 / (256·2^z)` пикселей. Ячейку берём около
 * сорока пикселей — примерно размер метки вместе с подписью.
 */
export function cellSizeForZoom(zoom: number, cellPx = 40): number {
  return (cellPx * 360) / (256 * 2 ** zoom);
}

export function clusterByGrid<T extends Located>(
  points: T[],
  cellDeg: number,
): Cluster<T>[] {
  if (cellDeg <= 0) {
    // Нулевая ячейка означала бы деление на ноль и один кластер на всё:
    // безопаснее считать, что группировать нечего.
    return points.map((p) => ({ lat: p.lat, lng: p.lng, items: [p] }));
  }

  const cells = new Map<string, T[]>();

  for (const p of points) {
    const key = `${Math.floor(p.lat / cellDeg)}:${Math.floor(p.lng / cellDeg)}`;
    const cell = cells.get(key);
    if (cell) cell.push(p);
    else cells.set(key, [p]);
  }

  return [...cells.values()].map((items) => ({
    lat: items.reduce((s, p) => s + p.lat, 0) / items.length,
    lng: items.reduce((s, p) => s + p.lng, 0) / items.length,
    items,
  }));
}
